import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { MapPin, ExternalLink, ChevronDown, ChevronRight, ChevronLeft, Heart, Sparkles, Camera, Check, MessageSquare } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import type {
  WeddingPersonDetails,
  WeddingEventItem,
  WeddingFamilyCouple,
  WeddingMediaItem,
  FamilyInvitationCard,
  BirthdaySectionBackground,
  Organizer,
  FamilyChild,
  WeddingCustomData,
} from '@/src/types';
import {
  getGuestMessages,
  postGuestMessage,
  reactToGuestMessage,
  uploadGuestPhoto,
} from '@/src/infrastructure/lib/api';
import type { GuestMessage } from '@/src/types';
import { useRsvp } from '@/src/modules/rsvp';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import type { RsvpStatus } from '@/src/modules/rsvp';
import { CircularGallery, type WeddingGalleryItem } from '../../components/ui/circular-gallery';
import { SectionBgImage, hasSectionBg } from '../BirthdayPremium/sections/SectionBgImage';
import { LiftVeil } from '../../experiences/scene/interactive/LiftVeil';
import { TrackVIntegration } from '../TrackVIntegration';
import './styles.css';

// ─── Registry ─────────────────────────────────────────────────────────────────

const META: TemplateMetadata = {
  id: 'traditional-wedding-interactive',
  name: 'Traditional Wedding — Interactive',
  occasionType: 'WEDDING',
  description:
    'Rich interactive wedding invitation: couple introduction, multi-event timeline, expandable family tree, family invitation cards, and integrated RSVP.',
  isPremium: true,
  tags: ['wedding', 'traditional', 'interactive', 'family-tree', 'timeline', 'premium'],
  colorScheme: { primary: '#8B1A1A', accent: '#C8A032', bg: '#FAF6F0' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatEventDate(iso: string): string {
  try {
    const d = parseFlexibleDate(iso);
    if (!d || isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatEventDateShort(iso: string): string {
  try {
    const d = parseFlexibleDate(iso);
    if (!d || isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function generateMockBlessings(count: number): GuestMessage[] {
  const names = [
    "Rahul Sharma", "Priya Patel", "Amit Verma", "Sunita Sharma", "Sanjay Mehta",
    "Kavita Mehta", "Neha Sen", "Vikram Singh", "Deepak Kapoor", "Anjali Gupta",
    "Rohan Malhotra", "Shalini Iyer", "Karan Johar", "Simran Kaur", "Arjun Reddy"
  ];
  const relations = ["Friend", "Family", "Colleague", "Cousin", "Well-wisher"];
  
  const smallMsgs = [
    "Congratulations!", "Best wishes to the couple!", "Wishing you all the best! 🎉",
    "Super happy for both of you! ❤️", "Cheers to a beautiful life ahead!"
  ];
  const medMsgs = [
    "Wishing you both a lifetime of love, happiness and togetherness.",
    "May your love grow stronger with each passing day. Congratulations on your wedding!",
    "So honored to be part of your special day. May your marriage be filled with laughter and endless love!",
    "Wishing you a wonderful journey as you build your new life together. Congrats! ✨"
  ];
  const largeMsgs = [
    "May this beautiful journey bring joy, prosperity and countless memories. May your bond grow stronger every day.",
    "Congratulations on taking this beautiful step! May the years ahead be filled with lasting joy, deep friendship, and wonderful adventures together as husband and wife. Sending you all our love and blessings.",
    "Wishing you both a magnificent wedding day followed by a life full of deep love, understanding, and shared dreams. May you always find comfort, support, and infinite happiness in each other's arms."
  ];

  const list: GuestMessage[] = [];
  for (let i = 0; i < count; i++) {
    const name = names[i % names.length];
    const suffix = i >= names.length ? ` ${Math.floor(i / names.length) + 1}` : '';
    const guestName = `${name}${suffix}`;
    const relationship = relations[i % relations.length];
    
    let message = "";
    if (i % 3 === 0) {
      message = smallMsgs[Math.floor(i / 3) % smallMsgs.length];
    } else if (i % 3 === 1) {
      message = medMsgs[Math.floor(i / 3) % medMsgs.length];
    } else {
      message = largeMsgs[Math.floor(i / 3) % largeMsgs.length];
    }

    list.push({
      id: `mock-stress-${i}`,
      eventType: 'wedding',
      guestName,
      relationship,
      message,
      createdAt: new Date(Date.now() - i * 60 * 1000).toISOString(),
      isApproved: true,
      showPublicly: true,
      reactions: {
        love: (i * 7) % 15,
        blessings: (i * 3) % 25,
        wishes: (i * 5) % 20
      }
    });
  }
  return list;
}

// ─── Scroll Reveal Hook ───────────────────────────────────────────────────────

function useReveal(delay = 0) {
  const ref = useRef<HTMLElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSeen(true);
      return;
    }
    let done = false;
    const finish = () => { if (!done) { done = true; setSeen(true); cleanup(); } };
    const check = () => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9 && r.bottom > 0) finish();
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    const t1 = setTimeout(check, 100);
    const t2 = setTimeout(check, 500);
    const safety = setTimeout(finish, 2500);
    check();
    function cleanup() {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(safety);
    }
    return cleanup;
  }, []);

  return {
    ref,
    className: `twi-reveal${seen ? ' twi-in' : ''}`,
    style: { transitionDelay: `${delay}ms` } as React.CSSProperties,
  };
}

// ─── Reveal Wrapper ───────────────────────────────────────────────────────────

interface RevealProps {
  children?: React.ReactNode;
  className?: string;
  delay?: number;
  as?: keyof React.JSX.IntrinsicElements;
}

const Reveal: React.FC<RevealProps> = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const { ref, className: rc, style } = useReveal(delay);
  const El = Tag as any;
  return (
    <El ref={ref} className={`${rc} ${className}`} style={style}>
      {children}
    </El>
  );
};

// ─── Floating Nav ─────────────────────────────────────────────────────────────

interface NavSection { id: string; label: string; }

const FloatingNav: React.FC<{ sections: NavSection[] }> = ({ sections }) => {
  const [active, setActive] = useState('');

  useEffect(() => {
    const ids = sections.map((s) => s.id);
    const handler = () => {
      const vh = window.innerHeight;
      let best = '';
      let bestDist = Infinity;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const dist = Math.abs(el.getBoundingClientRect().top - vh * 0.35);
        if (dist < bestDist) { bestDist = dist; best = id; }
      }
      setActive(best);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav className="twi-nav" aria-label="Page sections">
      {sections.map((s) => (
        <button
          key={s.id}
          className={`twi-nav-dot${active === s.id ? ' twi-active' : ''}`}
          data-label={s.label}
          onClick={() => scrollTo(s.id)}
          aria-label={`Go to ${s.label}`}
          type="button"
        />
      ))}
    </nav>
  );
};

// ─── Hero Section ─────────────────────────────────────────────────────────────

const PETALS = [
  { left: '8%',  delay: '0s',    dur: '7s',  color: '#C8A032', size: 9 },
  { left: '22%', delay: '1.5s',  dur: '9s',  color: '#8B1A1A', size: 7 },
  { left: '38%', delay: '3s',    dur: '8s',  color: '#D4688A', size: 11 },
  { left: '55%', delay: '0.8s',  dur: '10s', color: '#C8A032', size: 8 },
  { left: '70%', delay: '2.4s',  dur: '7.5s',color: '#8B1A1A', size: 6 },
  { left: '85%', delay: '4s',    dur: '9.5s',color: '#D4688A', size: 10 },
  { left: '14%', delay: '5.5s',  dur: '8.5s',color: '#C8A032', size: 7 },
  { left: '62%', delay: '6.2s',  dur: '11s', color: '#8B1A1A', size: 9 },
];

interface HeroProps {
  groomName: string;
  brideName: string;
  eventDate: string;
  eventVenue: string;
}

const CountdownTimer: React.FC<{ targetDate: Date | null }> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isPast: boolean;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        isPast: false,
      };
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  if (!targetDate) return null;

  const padZero = (n: number) => String(n).padStart(2, '0');

  if (timeLeft.isPast) {
    return (
      <div className="twi-countdown-past">
        <span className="twi-countdown-celebrate-text">Celebrating Our Togetherness!</span>
      </div>
    );
  }

  return (
    <div className="twi-countdown-container">
      <div className="twi-countdown-item">
        <span className="twi-countdown-num">{padZero(timeLeft.days)}</span>
        <span className="twi-countdown-label">Days</span>
      </div>
      <div className="twi-countdown-colon">:</div>
      <div className="twi-countdown-item">
        <span className="twi-countdown-num">{padZero(timeLeft.hours)}</span>
        <span className="twi-countdown-label">Hours</span>
      </div>
      <div className="twi-countdown-colon">:</div>
      <div className="twi-countdown-item">
        <span className="twi-countdown-num">{padZero(timeLeft.minutes)}</span>
        <span className="twi-countdown-label">Mins</span>
      </div>
      <div className="twi-countdown-colon">:</div>
      <div className="twi-countdown-item">
        <span className="twi-countdown-num">{padZero(timeLeft.seconds)}</span>
        <span className="twi-countdown-label">Secs</span>
      </div>
    </div>
  );
};

/**
 * Wedding introduction hero — shows the date + time prominently and
 * an "Add To Calendar" dropdown (Google Calendar / Apple-Outlook .ics).
 * No generic invitation text — the date IS the hero.
 */
const EventDetailsSection: React.FC<HeroProps> = ({ groomName, brideName, eventDate, eventVenue }) => {
  const [calOpen, setCalOpen] = useState(false);

  // Extract a human-readable time from the ISO date (e.g. "10:30 AM")
  const parsedDate = useMemo(() => {
    try {
      const d = new Date(eventDate);
      if (isNaN(d.getTime())) return null;
      return d;
    } catch { return null; }
  }, [eventDate]);

  const formattedTime = useMemo(() => {
    if (!parsedDate) return '';
    const h = parsedDate.getHours();
    const m = parsedDate.getMinutes();
    // Don't show time if it's exactly midnight (likely no time was set)
    if (h === 0 && m === 0) return '';
    return parsedDate.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  }, [parsedDate]);

  const hasDate = Boolean(parsedDate);

  /** Build Google Calendar URL */
  const openGoogleCal = useCallback(() => {
    if (!parsedDate) return;
    const pad = (n: number) => String(n).padStart(2, '0');
    const y = parsedDate.getFullYear();
    const mo = pad(parsedDate.getMonth() + 1);
    const dy = pad(parsedDate.getDate());
    const hr = pad(parsedDate.getHours());
    const mi = pad(parsedDate.getMinutes());
    const start = `${y}${mo}${dy}T${hr}${mi}00`;
    // Default 3-hour event
    const endD = new Date(parsedDate.getTime() + 3 * 60 * 60 * 1000);
    const end = `${endD.getFullYear()}${pad(endD.getMonth() + 1)}${pad(endD.getDate())}T${pad(endD.getHours())}${pad(endD.getMinutes())}00`;
    const title = encodeURIComponent(`${groomName} & ${brideName} — Wedding`);
    const loc = encodeURIComponent(eventVenue || '');
    const details = encodeURIComponent('You are cordially invited to our wedding celebration!');
    window.open(
      `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&location=${loc}&details=${details}`,
      '_blank',
    );
    setCalOpen(false);
  }, [parsedDate, groomName, brideName, eventVenue]);

  /** Download .ics file (Apple Calendar / Outlook) */
  const downloadIcs = useCallback(() => {
    if (!parsedDate) return;
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) =>
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
    const endD = new Date(parsedDate.getTime() + 3 * 60 * 60 * 1000);
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//InviteCloud//Wedding//EN',
      'BEGIN:VEVENT',
      `DTSTART:${fmt(parsedDate)}`,
      `DTEND:${fmt(endD)}`,
      `SUMMARY:${groomName} & ${brideName} — Wedding`,
      `LOCATION:${eventVenue || ''}`,
      'DESCRIPTION:You are cordially invited to our wedding celebration!',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${groomName}-${brideName}-Wedding.ics`;
    a.click();
    URL.revokeObjectURL(url);
    setCalOpen(false);
  }, [parsedDate, groomName, brideName, eventVenue]);

  return (
    <section className="twi-hero" aria-label="Wedding introduction">
      {/* decorative mandala SVG */}
      <svg
        className="twi-hero-mandala"
        viewBox="0 0 200 200"
        aria-hidden="true"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="100" cy="100" r="90" stroke="#8B1A1A" strokeWidth="1" />
        <circle cx="100" cy="100" r="70" stroke="#C8A032" strokeWidth="0.8" />
        <circle cx="100" cy="100" r="50" stroke="#8B1A1A" strokeWidth="0.8" />
        {Array.from({ length: 16 }, (_, i) => {
          const a = (i / 16) * Math.PI * 2;
          return (
            <line
              key={i}
              x1={100 + Math.cos(a) * 50}
              y1={100 + Math.sin(a) * 50}
              x2={100 + Math.cos(a) * 90}
              y2={100 + Math.sin(a) * 90}
              stroke="#C8A032"
              strokeWidth="0.6"
            />
          );
        })}
      </svg>

      {/* falling petals */}
      {PETALS.map((p, i) => (
        <span
          key={i}
          className="twi-petal"
          aria-hidden="true"
          style={{
            left: p.left,
            top: '-12px',
            width: p.size,
            height: p.size,
            background: p.color,
            animationDelay: p.delay,
            animationDuration: p.dur,
          }}
        />
      ))}

      <div className="twi-hero-inner">
        <Reveal>
          <span className="twi-eyebrow">Save the Date</span>
          <p className="twi-hero-date">{formatEventDate(eventDate)}</p>
          {formattedTime && (
            <p className="twi-hero-time">{formattedTime} onwards</p>
          )}
          {hasDate && <CountdownTimer targetDate={parsedDate} />}
        </Reveal>

        {hasDate && (
          <Reveal delay={200}>
            <div className="twi-cal-wrap">
              <button
                className="twi-hero-cta"
                onClick={() => setCalOpen((v) => !v)}
                type="button"
                aria-expanded={calOpen}
                aria-haspopup="true"
              >
                Add To Calendar
              </button>
              {calOpen && (
                <div className="twi-cal-dropdown" role="menu">
                  <button className="twi-cal-option" role="menuitem" onClick={openGoogleCal} type="button">
                    Google Calendar
                  </button>
                  <button className="twi-cal-option" role="menuitem" onClick={downloadIcs} type="button">
                    Apple / Outlook (.ics)
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>

      <div className="twi-scroll-cue" aria-hidden="true">
        <span>Scroll</span>
        <ChevronDown size={14} />
      </div>
    </section>
  );
};

// ─── Sacred Verse Intro Section ───────────────────────────────────────────────
// A clean, content-focused opening: an optional user-uploaded deity image
// (background removed client-side at upload time, rendered with no visible
// box), the two verses, a simple divider, and शुभविवाह. No generated
// decorative illustrations — just typography on a quiet background, with
// optional user-uploaded images for both the spiritual header and the
// section background.

interface SacredVerseSectionProps {
  bg?: BirthdaySectionBackground;
  welcomeMessage?: string;
  godImage?: string;
  useShubhVivahImage?: boolean;
  shubhVivahImage?: string;
}

const SacredVerseSection: React.FC<SacredVerseSectionProps> = ({
  bg, welcomeMessage, godImage, useShubhVivahImage, shubhVivahImage,
}) => {
  const showTitleImage = Boolean(useShubhVivahImage && shubhVivahImage);
  const onImageBg = hasSectionBg(bg);
  return (
    <LiftVeil primaryColor="#8B1A1A" secondaryColor="#C8A032" backgroundColor="#1C0608" textColor="#F7EED6">
      <section className={`twi-sv${onImageBg ? ' twi-sv-has-bg' : ''}`} aria-label="Sacred invocation">
        <SectionBgImage bg={bg} />
        {/* Guaranteed 40-60% darkening on top of any background image so verse
            text stays readable regardless of the editor's chosen overlay style. */}
        {onImageBg && <div className="twi-contrast-scrim" aria-hidden="true" />}
        <div className="twi-sv-glow" aria-hidden="true" />
        <div className="twi-sv-mandala" aria-hidden="true" />

        <div className="twi-sv-inner">
          {godImage?.trim() && (
            <Reveal as="div" className="twi-sv-god-wrap" delay={0}>
              <img src={godImage} alt="" className="twi-sv-god-img" />
            </Reveal>
          )}
          <Reveal as="p" className="twi-sv-verse" delay={260}>
            ॥ वक्रतुंड महाकाय सूर्यकोटि समप्रभ ॥
          </Reveal>
          <Reveal as="p" className="twi-sv-verse" delay={480}>
            ॥ निर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा ॥
          </Reveal>
          <Reveal as="div" className="twi-sv-divider" delay={700}>
            <span className="twi-sv-divider-line" aria-hidden="true" />
            <span className="twi-sv-divider-mark" aria-hidden="true">✦</span>
            <span className="twi-sv-divider-line" aria-hidden="true" />
          </Reveal>
          {showTitleImage ? (
            <Reveal as="div" className="twi-sv-title-img-wrap" delay={920}>
              <img src={shubhVivahImage} alt="शुभविवाह" className="twi-sv-title-img" />
            </Reveal>
          ) : (
            <Reveal as="h2" className="twi-sv-title" delay={920}>
              शुभविवाह
            </Reveal>
          )}
          {welcomeMessage?.trim() && (
            <Reveal as="p" className="twi-sv-welcome" delay={1140}>
              {welcomeMessage}
            </Reveal>
          )}
        </div>
      </section>
    </LiftVeil>
  );
};

// ─── Couple Section ───────────────────────────────────────────────────────────

/**
 * WeddingSwastikDivider — Theme-aware SVG Swastik with four sacred dots.
 *
 * Renders as a pure SVG ornament using the template's CSS custom properties,
 * so it automatically matches any wedding theme. Never shows a white rectangle.
 *
 * When `useCustomImage` is true and a `customImageUrl` is supplied, the custom
 * asset is blended on top with mix-blend-mode: multiply — the white background
 * of any uploaded PNG/JPG/WEBP becomes invisible.
 */
interface WeddingSwastikDividerProps {
  useCustomImage?: boolean;
  customImageUrl?: string;
}

const WeddingSwastikDivider: React.FC<WeddingSwastikDividerProps> = ({
  useCustomImage,
  customImageUrl,
}) => {
  const showCustomOverlay =
    useCustomImage && customImageUrl && customImageUrl.trim().length > 0;

  return (
    <div className="twi-swastik-divider" role="img" aria-label="Traditional auspicious swastik emblem with four sacred dots">
      <svg
        className="twi-swastik-svg"
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          {/* ── Gold foil gradient: light gold edge → rich gold → deep maroon ── */}
          <linearGradient id="twi-swastik-foil" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#F5E6A3" />
            <stop offset="25%"  stopColor="#E8C96A" />
            <stop offset="50%"  stopColor="#C8A032" />
            <stop offset="75%"  stopColor="#B8860B" />
            <stop offset="100%" stopColor="#8B1A1A" stopOpacity="0.75" />
          </linearGradient>

          {/* Reverse highlight gradient for emboss top-light illusion */}
          <linearGradient id="twi-swastik-highlight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"  stopColor="#FFFBEA" stopOpacity="0.5" />
            <stop offset="40%" stopColor="#F5E6A3" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#8B1A1A" stopOpacity="0" />
          </linearGradient>

          {/* Radial warm glow behind the symbol */}
          <radialGradient id="twi-swastik-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#F5E6A3" stopOpacity="0.30" />
            <stop offset="40%"  stopColor="#E8C96A" stopOpacity="0.14" />
            <stop offset="70%"  stopColor="#C8A032" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#C8A032" stopOpacity="0" />
          </radialGradient>

          {/* Dot gradient — maroon with gold sheen */}
          <radialGradient id="twi-dot-fill" cx="35%" cy="35%" r="65%">
            <stop offset="0%"  stopColor="#D4AF37" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#8B1A1A" />
            <stop offset="100%" stopColor="#6B0000" />
          </radialGradient>

          {/* Emboss filter — specular lighting for foil-stamped depth */}
          <filter id="twi-emboss" x="-8%" y="-8%" width="116%" height="116%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" result="blur" />
            <feSpecularLighting in="blur" surfaceScale="4" specularConstant="0.7" specularExponent="18" result="specLight">
              <fePointLight x="40" y="30" z="60" />
            </feSpecularLighting>
            <feComposite in="specLight" in2="SourceAlpha" operator="in" result="specClip" />
            <feComposite in="SourceGraphic" in2="specClip" operator="arithmetic" k1="0" k2="1" k3="0.35" k4="0" />
          </filter>

          {/* Subtle drop shadow for depth */}
          <filter id="twi-depth" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0.6" dy="1" stdDeviation="0.8" floodColor="#6B0000" floodOpacity="0.28" />
            <feDropShadow dx="-0.3" dy="-0.5" stdDeviation="0.5" floodColor="#FFFBEA" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* ── Ambient warm halo ── */}
        <circle cx="60" cy="60" r="56" fill="url(#twi-swastik-glow)" />

        {/* ── Double decorative border rings ── */}
        <circle
          cx="60" cy="60" r="54"
          fill="none"
          stroke="#C8A032"
          strokeWidth="0.4"
          strokeOpacity="0.20"
        />
        <circle
          cx="60" cy="60" r="50"
          fill="none"
          stroke="#C8A032"
          strokeWidth="0.5"
          strokeOpacity="0.14"
          strokeDasharray="2 5"
        />

        {/* ─── Swastik symbol with inner dots (卐) ─── */}
        <g transform="translate(60, 60)" filter="url(#twi-depth)">

          {/* Base foil layer */}
          <g fill="url(#twi-swastik-foil)" filter="url(#twi-emboss)">
            {/* Centre block */}
            <rect x="-5" y="-5" width="10" height="10" rx="2" />

            {/* Top arm + right hook */}
            <rect x="-5" y="-28" width="10" height="23" rx="2" />
            <rect x="5"  y="-28" width="17" height="10" rx="2" />

            {/* Right arm + bottom hook */}
            <rect x="5"  y="-5" width="23" height="10" rx="2" />
            <rect x="18" y="5"  width="10" height="17" rx="2" />

            {/* Bottom arm + left hook */}
            <rect x="-5" y="5"  width="10" height="23" rx="2" />
            <rect x="-22" y="18" width="17" height="10" rx="2" />

            {/* Left arm + top hook */}
            <rect x="-28" y="-5" width="23" height="10" rx="2" />
            <rect x="-28" y="-22" width="10" height="17" rx="2" />
          </g>

          {/* Highlight overlay — gives the "light catching" foil shimmer */}
          <g fill="url(#twi-swastik-highlight)" opacity="0.6">
            <rect x="-5" y="-5" width="10" height="10" rx="2" />
            <rect x="-5" y="-28" width="10" height="23" rx="2" />
            <rect x="5"  y="-28" width="17" height="10" rx="2" />
            <rect x="5"  y="-5" width="23" height="10" rx="2" />
            <rect x="18" y="5"  width="10" height="17" rx="2" />
            <rect x="-5" y="5"  width="10" height="23" rx="2" />
            <rect x="-22" y="18" width="17" height="10" rx="2" />
            <rect x="-28" y="-5" width="23" height="10" rx="2" />
            <rect x="-28" y="-22" width="10" height="17" rx="2" />
          </g>

          {/* ── Decorative diamond flourishes at each hook tip ── */}
          <g fill="#D4AF37" opacity="0.55">
            {/* Top-right hook tip */}
            <polygon points="21,-23 23,-20 21,-17 19,-20" />
            {/* Bottom-right hook tip */}
            <polygon points="23,21 20,23 23,25 26,23" />
            {/* Bottom-left hook tip */}
            <polygon points="-21,23 -23,20 -21,17 -19,20" />
            {/* Top-left hook tip */}
            <polygon points="-23,-21 -20,-23 -23,-25 -26,-23" />
          </g>

          {/* ── Four sacred dots — inside the quadrant spaces ── */}
          <g filter="url(#twi-emboss)">
            <circle cx="-12" cy="-12" r="3.2" fill="url(#twi-dot-fill)" />
            <circle cx="12"  cy="-12" r="3.2" fill="url(#twi-dot-fill)" />
            <circle cx="12"  cy="12"  r="3.2" fill="url(#twi-dot-fill)" />
            <circle cx="-12" cy="12"  r="3.2" fill="url(#twi-dot-fill)" />
          </g>
          {/* Dot highlight — tiny specular catchlight */}
          <g fill="#FFFBEA" opacity="0.35">
            <circle cx="-13" cy="-13" r="1" />
            <circle cx="11"  cy="-13" r="1" />
            <circle cx="11"  cy="11"  r="1" />
            <circle cx="-13" cy="11"  r="1" />
          </g>
        </g>
      </svg>

      {/* Optional custom image overlay — blended to hide white backgrounds */}
      {showCustomOverlay && (
        <img
          src={customImageUrl!.trim()}
          alt=""
          className="twi-swastik-custom-overlay"
          loading="lazy"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

interface PersonCardProps {
  person: WeddingPersonDetails;
  label: string;
  delay?: number;
}

const PersonCard: React.FC<PersonCardProps> = ({ person, label, delay = 0 }) => {
  const name = person.fullName || person.nickname || label;
  const parents = (person as any).parents
    || person.parentsEn
    || (person.fatherName && person.motherName
      ? `${person.fatherName} & ${person.motherName}`
      : person.fatherName || person.motherName || '');

  return (
    <Reveal className="twi-couple-card" delay={delay}>
      {person.photoUrl ? (
        <img
          src={person.photoUrl}
          alt={name}
          className="twi-person-photo"
          loading="lazy"
        />
      ) : (
        <div className="twi-person-photo-placeholder" aria-hidden="true">
          {label === 'Groom' ? '🤵' : '👰'}
        </div>
      )}
      <h2 className="twi-person-name">{name}</h2>
      {person.familyPositionText && (
        <p className="twi-person-position">{person.familyPositionText}</p>
      )}
      <div className="twi-person-family">
        {person.grandparentsNames && (
          <p style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>{person.grandparentsNames}</p>
        )}
        {parents && <p>{parents}</p>}
        {person.homeAddress && <p style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>{person.homeAddress}</p>}
      </div>
    </Reveal>
  );
};

interface CoupleProps {
  groom: WeddingPersonDetails;
  bride: WeddingPersonDetails;
  bg?: BirthdaySectionBackground;
  useCustomSwastikImage?: boolean;
  customSwastikImage?: string;
}

const CoupleSection: React.FC<CoupleProps> = ({ groom, bride, bg, useCustomSwastikImage, customSwastikImage }) => {
  const onImageBg = hasSectionBg(bg);
  return (
    <section
      id="couple"
      className={`twi-section twi-section-cream twi-couple-section${onImageBg ? ' twi-section-has-bg' : ''}`}
      aria-label="Couple introduction"
    >
      <SectionBgImage bg={bg} />
      {onImageBg && <div className="twi-contrast-scrim" aria-hidden="true" />}
      <div className="twi-couple-section-inner" style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <span className="twi-eyebrow">The Happy Couple</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>Bride and Groom</h2>
          <div className="twi-divider" />
        </Reveal>
      </div>
      <div className="twi-couple-grid twi-couple-section-inner">
        <PersonCard person={bride} label="Bride" delay={80} />
        <Reveal className="twi-couple-emblem-wrap" delay={120}>
          <WeddingSwastikDivider
            useCustomImage={useCustomSwastikImage}
            customImageUrl={customSwastikImage}
          />
        </Reveal>
        <PersonCard person={groom} label="Groom" delay={160} />
      </div>
    </section>
  );
};

// ─── Wedding Event Card ───────────────────────────────────────────────────────

interface EventCardProps {
  event: WeddingEventItem;
  index: number;
}

const EventCard: React.FC<EventCardProps> = memo(({ event, index }) => {
  const [expanded, setExpanded] = useState(false);
  const mapsUrl = event.mapsLink
    || (event.venue
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`
      : '');

  return (
    <Reveal className="twi-evt-card" delay={index * 100}>
      {/* Cover image — only rendered when user has uploaded a photo */}
      {event.coverImageUrl && (
        <div className="twi-evt-card-img-wrap">
          <img
            src={event.coverImageUrl}
            alt={event.title}
            className="twi-evt-card-img"
            loading="lazy"
          />
          <span className="twi-evt-card-badge">{event.title}</span>
        </div>
      )}

      {/* Card body */}
      <div className="twi-evt-card-body">
        <h3 className="twi-evt-card-title">{event.title}</h3>

        <div className="twi-evt-card-meta">
          <p className="twi-evt-card-date">{formatEventDateShort(event.date)}</p>
          {(event.startTime || event.endTime) && (
            <p className="twi-evt-card-time">
              {event.startTime}
              {event.endTime && ` – ${event.endTime}`}
            </p>
          )}
        </div>

        {event.venue && (
          <p className="twi-evt-card-venue">
            <MapPin size={12} className="twi-evt-card-venue-icon" />
            {event.venue}
          </p>
        )}

        {event.description && (
          <>
            <p className={`twi-evt-card-desc${expanded ? ' twi-expanded' : ''}`}>
              {event.description}
            </p>
            <button
              className="twi-evt-card-toggle"
              onClick={() => setExpanded((v) => !v)}
              type="button"
              aria-expanded={expanded}
            >
              {expanded ? 'Less' : 'Read more'}
              <ChevronDown
                size={10}
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.3s',
                }}
              />
            </button>
          </>
        )}

        {/* Get Directions CTA — only when a map URL is available */}
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="twi-evt-card-cta"
            aria-label={`Get directions to ${event.title}`}
          >
            <MapPin size={13} />
            Get Directions
            <ExternalLink size={10} />
          </a>
        )}
      </div>
    </Reveal>
  );
});
EventCard.displayName = 'EventCard';

// ─── Events Card Grid Section ─────────────────────────────────────────────────

interface EventsProps {
  events: WeddingEventItem[];
}

const SAMPLE_EVENTS: WeddingEventItem[] = [
  {
    title: 'Haldi',
    date: new Date(Date.now() + 86400000).toISOString(),
    startTime: '10:00 AM',
    venue: 'Host Residence',
    description: 'Turmeric ceremony. Wear yellow!',
  },
  {
    title: 'Sangeet',
    date: new Date(Date.now() + 86400000).toISOString(),
    startTime: '7:30 PM',
    venue: 'Host Residence',
    description: 'An evening of song and dance.',
  },
  {
    title: 'Wedding Ceremony',
    date: new Date(Date.now() + 172800000).toISOString(),
    startTime: '11:00 AM',
    venue: 'Wedding Venue',
    description: 'The main wedding ceremony.',
  },
  {
    title: 'Reception',
    date: new Date(Date.now() + 172800000).toISOString(),
    startTime: '7:00 PM',
    venue: 'Wedding Venue',
    description: 'Dinner and celebrations.',
  },
];

const EventsSection: React.FC<EventsProps> = ({ events }) => {
  const list = (events.length > 0 ? events : SAMPLE_EVENTS).filter(
    (evt) => evt.visible !== false,
  );

  if (list.length === 0) return null;

  return (
    <section id="events" className="twi-section" aria-label="Wedding events">
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Reveal>
          <span className="twi-eyebrow">The Celebrations</span>
          <h2
            className="twi-section-title"
            style={{ color: 'var(--twi-maroon)' }}
          >
            Celebration Journey
          </h2>
          <div className="twi-divider" />
        </Reveal>
      </div>
      <div className="twi-evt-grid">
        {list.map((evt, i) => (
          <EventCard key={`${evt.title}-${i}`} event={evt} index={i} />
        ))}
      </div>
    </section>
  );
};

// ─── Family Couple Row ────────────────────────────────────────────────────────

const SAMPLE_GROOM_COUPLES: WeddingFamilyCouple[] = [
  { id: 'gg1', mrsName: 'Savitribai Sharma', mrName: 'Ramchandra Sharma', label: 'Paternal Grandparents' },
  { id: 'gp1', mrsName: 'Sunita Sharma',     mrName: 'Vijay Sharma',      label: 'Parents' },
];

const SAMPLE_BRIDE_COUPLES: WeddingFamilyCouple[] = [
  { id: 'bg1', mrsName: 'Padmavati Mehta', mrName: 'Balkrishna Mehta', label: 'Paternal Grandparents' },
  { id: 'bp1', mrsName: 'Asha Mehta',      mrName: 'Suresh Mehta',     label: 'Parents' },
];

interface CoupleRowProps {
  couple: WeddingFamilyCouple;
  delay?: number;
}

const CoupleRow: React.FC<CoupleRowProps> = memo(({ couple, delay = 0 }) => {
  const isSingle = couple.type === 'single';
  return (
    <Reveal className="twi-couple-row" delay={delay}>
      {isSingle ? (
        <div className="twi-single-row-inner flex justify-center items-center py-2">
          <span className="twi-single-name">
            {couple.singleName || couple.mrName || couple.mrsName || '—'}
          </span>
        </div>
      ) : (
        <div className="twi-couple-row-inner">
          <div className="twi-couple-half twi-couple-mrs">
            <span className="twi-couple-title">Mrs.</span>
            <span className="twi-couple-name">{couple.mrsName || '—'}</span>
          </div>
          <div className="twi-couple-connector" aria-hidden="true">
            <span className="twi-couple-line" />
            <span className="twi-couple-heart">♥</span>
            <span className="twi-couple-line" />
          </div>
          <div className="twi-couple-half twi-couple-mr">
            <span className="twi-couple-title">Mr.</span>
            <span className="twi-couple-name">{couple.mrName || '—'}</span>
          </div>
        </div>
      )}
      {couple.label && <p className="twi-couple-label">{couple.label}</p>}
    </Reveal>
  );
});
CoupleRow.displayName = 'CoupleRow';

export function guessGenderByName(name: string): 'boy' | 'girl' {
  const n = (name || '').trim().toLowerCase();
  if (!n) return 'boy'; // default
  
  // Common girl name suffixes in Indian and general names:
  // e.g. names ending in 'a' (Kiara, Anaya, Pooja, Priya), 'i' (Aarti, Shruti), 'e' (Grace, Annie)
  const girlEndings = ['a', 'i', 'e', 'ee', 'ya', 'ha', 'ma', 'ta', 'na', 'ra', 'sha'];
  const girlKeywords = ['kiara', 'anaya', 'priya', 'rhea', 'isha', 'dia', 'ria', 'siya', 'tanya', 'navya', 'kavya', 'sanya', 'anya'];
  
  if (girlKeywords.some(kw => n.includes(kw))) return 'girl';
  
  const lastChar = n.charAt(n.length - 1);
  const lastTwo = n.slice(-2);
  
  if (girlEndings.includes(lastChar) || girlEndings.includes(lastTwo)) {
    // some exceptions for boys names ending in 'a' (e.g. Krishna, Rama, Shiva)
    const boyExceptions = ['shiva', 'krishna', 'rama', 'rishi', 'hari', 'adi'];
    if (boyExceptions.includes(n)) {
      return 'boy';
    }
    return 'girl';
  }
  
  return 'boy';
}

// ─── Family Tree Section ──────────────────────────────────────────────────────

interface FamilyTreeProps {
  groomCouples: WeddingFamilyCouple[];
  brideCouples: WeddingFamilyCouple[];
  showGroom: boolean;
  showBride: boolean;
}

type FamilySide = 'groom' | 'bride';

const FamilyTreeSection: React.FC<FamilyTreeProps> = ({ groomCouples, brideCouples, showGroom, showBride }) => {
  const groomList = showGroom ? (groomCouples.length > 0 ? groomCouples : SAMPLE_GROOM_COUPLES) : [];
  const brideList = showBride ? (brideCouples.length > 0 ? brideCouples : SAMPLE_BRIDE_COUPLES) : [];

  const effectiveHasGroom = showGroom && groomList.length > 0;
  const effectiveHasBride = showBride && brideList.length > 0;

  if (!effectiveHasGroom && !effectiveHasBride) return null;

  const defaultSide: FamilySide = effectiveHasGroom ? 'groom' : 'bride';
  const [activeSide, setActiveSide] = useState<FamilySide>(defaultSide);
  const bothAvailable = effectiveHasGroom && effectiveHasBride;
  
  const activeSideDerived = bothAvailable ? activeSide : (effectiveHasGroom ? 'groom' : 'bride');
  const activeList = activeSideDerived === 'groom' ? groomList : brideList;
  const sideLabel = activeSideDerived === 'groom' ? "Groom's Family" : "Bride's Family";

  // Sort sequence: single/late first, married couple second
  const sortedActiveList = [...activeList].sort((a, b) => {
    const aSingle = a.type === 'single';
    const bSingle = b.type === 'single';
    if (aSingle && !bSingle) return -1;
    if (!aSingle && bSingle) return 1;
    return 0;
  });

  return (
    <section id="family-tree" className="twi-section twi-section-cream" aria-label="Family">
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <span className="twi-eyebrow">Our Roots</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            Family
          </h2>
          <div className="twi-divider" />
        </Reveal>
        {bothAvailable && (
          <Reveal delay={60}>
            <div className="twi-tree-side-toggle" role="group" aria-label="Family side">
              <button
                type="button"
                className={`twi-tree-side-btn${activeSideDerived === 'groom' ? ' twi-active' : ''}`}
                onClick={() => setActiveSide('groom')}
                aria-pressed={activeSideDerived === 'groom'}
              >
                Groom's Family
              </button>
              <button
                type="button"
                className={`twi-tree-side-btn${activeSideDerived === 'bride' ? ' twi-active' : ''}`}
                onClick={() => setActiveSide('bride')}
                aria-pressed={activeSideDerived === 'bride'}
              >
                Bride's Family
              </button>
            </div>
          </Reveal>
        )}
        {!bothAvailable && (
          <Reveal delay={60}>
            <p style={{ color: 'var(--twi-maroon)', fontSize: 14, fontWeight: 600, marginTop: 8 }}>
              {sideLabel}
            </p>
          </Reveal>
        )}
      </div>
      <div className="twi-couple-list" role="list">
        {sortedActiveList.map((couple, i) => (
          <div key={couple.id} role="listitem">
            <CoupleRow couple={couple} delay={i * 80} />
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── Organizers Section ───────────────────────────────────────────────────────

interface OrganizersProps {
  organizers: Organizer[];
  showOrganizers: boolean;
}

const OrganizersSection: React.FC<OrganizersProps> = ({ organizers, showOrganizers }) => {
  if (!showOrganizers || organizers.length === 0) return null;

  const hasMultiple = organizers.length > 1;

  return (
    <section id="organizers" className="twi-section twi-section-cream" aria-label="Organizers">
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <span className="twi-eyebrow">Wedding Organizers</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            Wedding Hosts
          </h2>
          <div className="twi-divider" />
        </Reveal>
      </div>

      {!hasMultiple ? (
        // Single Organizer Banner
        <Reveal className="twi-organizers-single-banner">
          <div className="twi-organizers-single-border-top" />
          <div className="twi-organizers-single-content">
            <span className="twi-organizer-single-tag">{organizers[0].designation || 'Main Organizer'}</span>
            <h3 className="twi-organizer-single-name">{organizers[0].name}</h3>
          </div>
          <div className="twi-organizers-single-border-bottom" />
        </Reveal>
      ) : (
        // Multiple Organizers Banner
        <div className="twi-organizers-multi-container">
          {/* Cards list */}
          <div className="twi-organizers-grid">
            {organizers.map((org, i) => (
              <Reveal key={org.id} className="twi-organizer-card" delay={i * 80}>
                <div className="twi-organizer-details">
                  <h3 className="twi-organizer-name">{org.name}</h3>
                  {org.designation && <p className="twi-organizer-designation">{org.designation}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Family Children Section ──────────────────────────────────────────────────

interface FamilyChildrenProps {
  childrenList: FamilyChild[];
  showChildren: boolean;
  title: string;
}

const FamilyChildrenSection: React.FC<FamilyChildrenProps> = ({ childrenList, showChildren, title }) => {
  if (!showChildren || childrenList.length === 0) return null;

  return (
    <section id="family-children" className="twi-section" aria-label="Little Stars">
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <span className="twi-eyebrow">Young Hearts</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            {title || 'Our Little Stars'}
          </h2>
          <div className="twi-divider" />
        </Reveal>
      </div>

      <div className="twi-children-grid">
        {childrenList.map((child, i) => {
          const isBoy = child.gender === 'boy' || (!child.gender && guessGenderByName(child.name) === 'boy');
          return (
            <Reveal key={child.id} className="twi-child-card" delay={i * 70}>
              {child.photo ? (
                <img src={child.photo} alt={child.name} className="twi-child-photo" loading="lazy" />
              ) : (
                <div className={`twi-child-photo-placeholder twi-child-gender-${isBoy ? 'boy' : 'girl'}`}>
                  {isBoy ? '👦' : '👧'}
                </div>
              )}
              <h3 className="twi-child-name">{child.name}</h3>
              <span className="twi-child-blessing">Blessings & Love</span>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

// ─── Blessings Wall Section ──────────────────────────────────────────────────

interface BlessingsWallProps {
  invitationId?: string | null;
  isLive?: boolean;
  title: string;
  allowPhotos: boolean;
  allowReactions: boolean;
  enableMockBlessings?: boolean;
  mockBlessingsCount?: number;
  loadMode?: 'loadMore' | 'infiniteScroll';
}

const SAMPLE_BLESSINGS: GuestMessage[] = [
  {
    id: 'sample-1',
    eventType: 'wedding',
    guestName: 'Sunita & Ramesh Sharma',
    relationship: 'Aunt & Uncle',
    message: 'Wishing you both a lifetime of love, laughter, and endless happiness. May your marriage be filled with beautiful moments! 💖✨',
    createdAt: new Date().toISOString(),
    isApproved: true,
    showPublicly: true,
    reactions: { love: 5, blessings: 12, wishes: 8 }
  },
  {
    id: 'sample-2',
    eventType: 'wedding',
    guestName: 'Amit Verma',
    relationship: 'Friend',
    message: 'Congratulations! So happy to celebrate this beautiful day with you both. Have a wonderful journey ahead! 🥂🎉',
    createdAt: new Date().toISOString(),
    isApproved: true,
    showPublicly: true,
    reactions: { love: 8, blessings: 4, wishes: 15 }
  },
  {
    id: 'sample-3',
    eventType: 'wedding',
    guestName: 'Neha & Rohan Mehta',
    relationship: 'Cousins',
    message: 'Welcome to the family, bhabhi! Wishing you two the absolute best. Cheers to your new adventure! ❤️💍',
    createdAt: new Date().toISOString(),
    isApproved: true,
    showPublicly: true,
    reactions: { love: 14, blessings: 9, wishes: 11 }
  }
];

const BlessingsWallSection: React.FC<BlessingsWallProps> = ({
  invitationId,
  isLive,
  title,
  allowPhotos,
  allowReactions,
  enableMockBlessings,
  mockBlessingsCount = 50,
  loadMode = 'loadMore',
}) => {
  const seeded = SAMPLE_BLESSINGS;
  const [messages, setMessages] = useState<GuestMessage[]>([]);
  const [allMockMessages, setAllMockMessages] = useState<GuestMessage[]>([]);
  
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('Friend');
  const [message, setMessage] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const LIMIT = 20;
  const isPreview = !invitationId || invitationId.startsWith('preview-') || invitationId.startsWith('live-preview-') || invitationId.startsWith('draft-preview-');
  const live = Boolean(isLive && invitationId && !isPreview);

  useEffect(() => {
    if (enableMockBlessings || isPreview) {
      const mocks = generateMockBlessings(mockBlessingsCount);
      setAllMockMessages(mocks);
      setMessages(mocks.slice(0, LIMIT));
      setOffset(LIMIT);
      setHasMore(mockBlessingsCount > LIMIT);
      setError(null);
    } else if (live && invitationId) {
      setLoading(true);
      setError(null);
      getGuestMessages(invitationId, 0, LIMIT)
        .then((rows) => {
          setMessages(rows);
          setOffset(rows.length);
          setHasMore(rows.length === LIMIT);
        })
        .catch(() => {
          setError('Could not load blessings.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setMessages(seeded);
      setOffset(seeded.length);
      setHasMore(false);
    }
  }, [enableMockBlessings, isPreview, mockBlessingsCount, live, invitationId]);

  const handleLoadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    if (enableMockBlessings || isPreview) {
      setTimeout(() => {
        const nextOffset = offset + LIMIT;
        setMessages(allMockMessages.slice(0, nextOffset));
        setOffset(nextOffset);
        setHasMore(mockBlessingsCount > nextOffset);
        setLoading(false);
      }, 300);
    } else if (live && invitationId) {
      try {
        const nextRows = await getGuestMessages(invitationId, offset, LIMIT);
        setMessages((prev) => [...prev, ...nextRows]);
        setOffset((prev) => prev + nextRows.length);
        setHasMore(nextRows.length === LIMIT);
      } catch (err) {
        setError('Could not load more blessings.');
      } finally {
        setLoading(false);
      }
    }
  }, [loading, hasMore, enableMockBlessings, isPreview, allMockMessages, offset, mockBlessingsCount, live, invitationId]);

  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (loadMode !== 'infiniteScroll' || !hasMore || loading) return;
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        void handleLoadMore();
      }
    }, { threshold: 0.1 });

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [loadMode, hasMore, loading, handleLoadMore]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim() || submitting) return;
    setSubmitting(true);
    setError(null);

    let uploadedPhotoUrl = '';
    if (photoFile && live && invitationId) {
      try {
        const uploadRes = await uploadGuestPhoto(invitationId, photoFile);
        uploadedPhotoUrl = uploadRes.processedUrl;
      } catch (err) {
        setError('Failed to upload image. Please try again.');
        setSubmitting(false);
        return;
      }
    } else if (photoFile && photoPreview) {
      uploadedPhotoUrl = photoPreview;
    }

    const payload = {
      guestName: name.trim(),
      message: message.trim(),
      relationship: relationship,
      photoUrl: uploadedPhotoUrl || undefined,
    };

    if (enableMockBlessings || !live || !invitationId) {
      const mockMsg: GuestMessage = {
        id: `mock-${Date.now()}`,
        eventType: 'wedding',
        guestName: payload.guestName,
        message: payload.message,
        relationship: payload.relationship,
        photoUrl: payload.photoUrl,
        createdAt: new Date().toISOString(),
        isApproved: true,
        showPublicly: true,
        reactions: {},
      };
      setMessages((prev) => [mockMsg, ...prev]);
      setName('');
      setMessage('');
      setPhotoFile(null);
      setPhotoPreview(null);
      setSubmitted(true);
      setSubmitting(false);
      setTimeout(() => setSubmitted(false), 3000);
      return;
    }

    try {
      const saved = await postGuestMessage(invitationId, payload);
      if (saved.isApproved) {
        setMessages((prev) => [saved, ...prev]);
      }
      setName('');
      setMessage('');
      setPhotoFile(null);
      setPhotoPreview(null);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send blessing. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReact = async (messageId: string, type: string) => {
    if (!allowReactions) return;
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === messageId) {
          const nextReactions = { ...msg.reactions };
          nextReactions[type] = (nextReactions[type] || 0) + 1;
          return { ...msg, reactions: nextReactions };
        }
        return msg;
      })
    );

    if (live && !enableMockBlessings) {
      try {
        await reactToGuestMessage(messageId, type);
      } catch (err) {}
    }
  };

  const featuredMessages = useMemo(() => {
    return messages.slice(0, 6);
  }, [messages]);

  const showFeatured = messages.length > 6;

  const featuredIds = useMemo(() => {
    return new Set(featuredMessages.map(msg => msg.id));
  }, [featuredMessages]);

  const allBlessingsToRender = useMemo(() => {
    if (showFeatured) {
      return messages.filter(msg => !featuredIds.has(msg.id));
    }
    return messages;
  }, [messages, showFeatured, featuredIds]);

  return (
    <section id="blessings-wall" className="twi-section twi-section-cream" aria-label="Blessings Wall">
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <span className="twi-eyebrow">A Wall of Love</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            {title || 'Blessings For The Couple'}
          </h2>
          <div className="twi-divider" />
        </Reveal>
      </div>

      <div className="twi-blessings-container">
        <Reveal className="twi-blessings-form-card" delay={80}>
          <h3 className="twi-blessings-form-title">Send Your Blessings</h3>
          <form onSubmit={handleSubmit} className="twi-blessings-form">
            <div className="twi-form-group">
              <label className="twi-form-label">Your Name</label>
              <input
                type="text"
                className="twi-form-input"
                placeholder="e.g. Aunt Sharma"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                required
              />
            </div>

            <div className="twi-form-group">
              <label className="twi-form-label">Your Relationship</label>
              <select
                className="twi-form-input"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
              >
                <option value="Family">Family</option>
                <option value="Friend">Friend</option>
                <option value="Colleague">Colleague</option>
                <option value="Well-wisher">Well-wisher</option>
                <option value="Cousin">Cousin</option>
              </select>
            </div>

            <div className="twi-form-group">
              <label className="twi-form-label">Your Message</label>
              <textarea
                className="twi-form-textarea"
                placeholder="Write your blessings and wishes..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                rows={4}
                required
              />
            </div>

            {allowPhotos && (
              <div className="twi-form-group">
                <label className="twi-form-label">Attach Photo (Optional)</label>
                {!photoPreview ? (
                  <div className="twi-photo-upload-zone">
                    <input
                      type="file"
                      id="blessing-photo-file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                    <label htmlFor="blessing-photo-file" className="twi-photo-upload-trigger">
                      <Camera size={20} className="text-gray-400" />
                      <span>Upload a Photo</span>
                    </label>
                  </div>
                ) : (
                  <div className="twi-photo-preview-wrap">
                    <img src={photoPreview} alt="Preview" className="twi-photo-preview" />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="twi-photo-remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && <p className="twi-form-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="twi-blessings-submit-btn"
            >
              {submitting ? 'Sending...' : 'Send Blessing'}
            </button>

            {submitted && (
              <p className="twi-form-success">
                <Check size={16} /> Blessing sent successfully!
              </p>
            )}
          </form>
        </Reveal>

        <div className="twi-blessings-wall-panel space-y-12">
          {messages.length === 0 ? (
            <div className="twi-blessings-empty">
              <p>No messages visible yet.</p>
            </div>
          ) : (
            <>
              {showFeatured && (
                <div className="twi-featured-blessings-section">
                  <h3 className="twi-blessings-subtitle text-left font-serif mb-6 text-xl tracking-wider italic text-amber-600 border-b pb-2 border-amber-600/20">✨ Highlighted Blessings</h3>
                  <div className="twi-blessings-masonry">
                    {featuredMessages.map((msg, i) => (
                      <Reveal key={`feat-${msg.id}`} className="twi-blessings-card twi-featured-card" delay={i * 50}>
                        {msg.photoUrl && (
                          <img src={msg.photoUrl} alt={msg.guestName} className="twi-blessing-photo" loading="lazy" />
                        )}
                        <p className="twi-blessing-message text-lg leading-relaxed">"{msg.message}"</p>
                        
                        <div className="twi-blessing-card-divider">
                          <Sparkles size={16} className="text-amber-500 animate-pulse" />
                        </div>

                        <div className="twi-blessing-footer">
                          <div className="twi-blessing-author-info">
                            <div className="twi-blessing-avatar">
                              {msg.guestName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="twi-blessing-author text-base">{msg.guestName}</h4>
                              <span className="twi-blessing-relation">{msg.relationship || 'Guest'}</span>
                            </div>
                          </div>

                          {allowReactions && (
                            <div className="twi-blessing-reactions">
                              <button
                                onClick={() => handleReact(msg.id, 'love')}
                                className="twi-reaction-btn"
                              >
                                ❤️ <span className="twi-reaction-count">{msg.reactions?.love || 0}</span>
                              </button>
                              <button
                                onClick={() => handleReact(msg.id, 'blessings')}
                                className="twi-reaction-btn"
                              >
                                🙏 <span className="twi-reaction-count">{msg.reactions?.blessings || 0}</span>
                              </button>
                              <button
                                onClick={() => handleReact(msg.id, 'wishes')}
                                className="twi-reaction-btn"
                              >
                                ✨ <span className="twi-reaction-count">{msg.reactions?.wishes || 0}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}

              {messages.length > 0 && (
                <div className="twi-all-blessings-section">
                  <h3 className="twi-blessings-subtitle text-left font-serif mb-6 text-xl tracking-wider italic text-amber-600 border-b pb-2 border-amber-600/20">💌 All Blessings</h3>
                  <div className="twi-blessings-masonry">
                    {allBlessingsToRender.map((msg, i) => (
                      <Reveal key={msg.id} className="twi-blessings-card" delay={i * 20}>
                        {msg.photoUrl && (
                          <img src={msg.photoUrl} alt={msg.guestName} className="twi-blessing-photo" loading="lazy" />
                        )}
                        <p className="twi-blessing-message">"{msg.message}"</p>
                        
                        <div className="twi-blessing-card-divider">
                          <Sparkles size={12} className="text-amber-500" />
                        </div>

                        <div className="twi-blessing-footer">
                          <div className="twi-blessing-author-info">
                            <div className="twi-blessing-avatar">
                              {msg.guestName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="twi-blessing-author">{msg.guestName}</h4>
                              <span className="twi-blessing-relation">{msg.relationship || 'Guest'}</span>
                            </div>
                          </div>

                          {allowReactions && (
                            <div className="twi-blessing-reactions">
                              <button
                                onClick={() => handleReact(msg.id, 'love')}
                                className="twi-reaction-btn"
                              >
                                ❤️ <span className="twi-reaction-count">{msg.reactions?.love || 0}</span>
                              </button>
                              <button
                                onClick={() => handleReact(msg.id, 'blessings')}
                                className="twi-reaction-btn"
                              >
                                🙏 <span className="twi-reaction-count">{msg.reactions?.blessings || 0}</span>
                              </button>
                              <button
                                onClick={() => handleReact(msg.id, 'wishes')}
                                className="twi-reaction-btn"
                              >
                                ✨ <span className="twi-reaction-count">{msg.reactions?.wishes || 0}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </Reveal>
                    ))}
                  </div>

                  {hasMore && (
                    <div className="text-center mt-8 pt-4">
                      {loadMode === 'loadMore' ? (
                        <button
                          onClick={handleLoadMore}
                          disabled={loading}
                          className="twi-load-more-btn px-6 py-3 border border-amber-600/40 text-amber-700 bg-white rounded-full font-semibold transition-all hover:bg-amber-50 hover:border-amber-600 active:scale-95"
                        >
                          {loading ? 'Loading...' : 'Load More Blessings'}
                        </button>
                      ) : (
                        <div ref={loadMoreTriggerRef} className="py-4 text-sm text-stone-500 italic">
                          {loading ? 'Loading more blessings...' : 'Scroll down to load more blessings'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Invitation Card ──────────────────────────────────────────────────────────

interface InvCardProps {
  card: FamilyInvitationCard;
  delay?: number;
}

const InvCard: React.FC<InvCardProps> = ({ card, delay = 0 }) => (
  <Reveal className="twi-fi-card" delay={delay}>
    {card.photoUrl ? (
      <img src={card.photoUrl} alt={card.name} className="twi-fi-photo" loading="lazy" />
    ) : (
      <div className="twi-fi-photo-placeholder" aria-hidden="true">🙏</div>
    )}
    <h3 className="twi-fi-name">{card.name}</h3>
    {card.relation && <p className="twi-fi-relation">{card.relation}</p>}
    {card.message && <p className="twi-fi-message">"{card.message}"</p>}
  </Reveal>
);

// ─── Family Invitations Section ───────────────────────────────────────────────

interface InvitationsProps {
  cards: FamilyInvitationCard[];
}

const SAMPLE_INVITATIONS: FamilyInvitationCard[] = [
  { id: '1', name: 'Uncle Ramesh', relation: 'Paternal Uncle', message: 'Please join us and celebrate this special occasion with our family.' },
  { id: '2', name: 'Aunt Meena',   relation: 'Maternal Aunt',  message: 'Your presence will make this day even more memorable for us.' },
  { id: '3', name: 'Cousin Priya', relation: 'Cousin',         message: 'We look forward to celebrating this joyous occasion together.' },
];

const FamilyInvitationsSection: React.FC<InvitationsProps> = ({ cards }) => {
  if (cards.length === 0) return null;
  const list = cards;
  const [offset, setOffset] = useState(0);
  const useCarousel = list.length > 4;

  const prev = useCallback(() => setOffset((o) => Math.max(0, o - 1)), []);
  const next = useCallback(() => setOffset((o) => Math.min(list.length - 1, o + 1)), [list.length]);

  return (
    <section id="invitations" className="twi-section" aria-label="Family invitations">
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Reveal>
          <span className="twi-eyebrow">Family Invitation</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            With Love From Our Family
          </h2>
          <div className="twi-divider" />
        </Reveal>
      </div>

      {useCarousel ? (
        <div className="twi-fi-carousel">
          <button
            className="twi-fi-carousel-btn prev"
            onClick={prev}
            disabled={offset === 0}
            aria-label="Previous family member"
            type="button"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="twi-fi-carousel-track" style={{ transform: `translateX(calc(-${offset} * (280px + 24px)))` }}>
            {list.map((card, i) => (
              <InvCard key={card.id} card={card} delay={i * 60} />
            ))}
          </div>
          <button
            className="twi-fi-carousel-btn next"
            onClick={next}
            disabled={offset >= list.length - 1}
            aria-label="Next family member"
            type="button"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      ) : (
        <div className="twi-fi-grid">
          {list.map((card, i) => (
            <InvCard key={card.id} card={card} delay={i * 70} />
          ))}
        </div>
      )}
    </section>
  );
};

// ─── Pre-Wedding Story Section ────────────────────────────────────────────────

interface PreWeddingStoryProps {
  title?: string;
  description?: string;
  moments: WeddingMediaItem[];
}

const PreWeddingStorySection: React.FC<PreWeddingStoryProps> = ({ title, description, moments }) => {
  const visibleMoments = moments.filter((m) => m.url);

  return (
    <section id="pre-wedding" className="twi-section" aria-label="Our story">
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Reveal>
          <span className="twi-eyebrow">Our Journey</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            {title || 'Our Story'}
          </h2>
          <div className="twi-divider" />
        </Reveal>
        {description && (
          <Reveal delay={80}>
            <p className="twi-story-desc">{description}</p>
          </Reveal>
        )}
      </div>
      {visibleMoments.length > 0 && (
        <div className="twi-moments-grid">
          {visibleMoments.map((moment, i) => (
            <Reveal key={i} className="twi-moment-item" delay={i * 80}>
              {moment.type === 'video' ? (
                <video
                  src={moment.url}
                  controls
                  className="twi-moment-media"
                  preload="metadata"
                  aria-label={moment.caption || `Story moment ${i + 1}`}
                />
              ) : (
                <img
                  src={moment.url}
                  alt={moment.caption || `Story moment ${i + 1}`}
                  className="twi-moment-media"
                  loading="lazy"
                />
              )}
              {(moment.caption || moment.description) && (
                <div className="twi-moment-caption">
                  {moment.caption && <p className="twi-moment-cap-title">{moment.caption}</p>}
                  {moment.description && <p className="twi-moment-cap-desc">{moment.description}</p>}
                </div>
              )}
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
};

// ─── Media Showcase / Circular Couple Gallery Section ─────────────────────────

interface MediaShowcaseProps {
  items: WeddingMediaItem[];
}

function toGalleryItems(items: WeddingMediaItem[]): WeddingGalleryItem[] {
  return items
    .filter((i) => i.url)
    .map((i) => ({
      imageUrl: i.url,
      title: i.caption?.trim() || undefined,
      subtitle: i.description?.trim() || undefined,
      isVideo: i.type === 'video',
    }));
}

const MediaShowcaseSection: React.FC<MediaShowcaseProps> = ({ items }) => {
  const galleryItems = useMemo(() => {
    return toGalleryItems(items);
  }, [items]);
  const [lightbox, setLightbox] = useState<string | null>(null);

  if (galleryItems.length === 0) return null;

  return (
    <section id="gallery" className="twi-section twi-section-cream" aria-label="Couple photo gallery">
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <Reveal>
          <span className="twi-eyebrow">Memories Together</span>
          <h2 className="twi-section-title" style={{ color: 'var(--twi-maroon)' }}>
            Gallery
          </h2>
          <div className="twi-divider" />
        </Reveal>
      </div>

      <Reveal delay={80}>
        <CircularGallery
          items={galleryItems}
          onItemActivate={(item) => {
            if (!item.isVideo) setLightbox(item.imageUrl);
          }}
        />
      </Reveal>

      {lightbox && (
        <div
          className="twi-lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Photo enlarged view"
          onClick={() => setLightbox(null)}
        >
          <button
            className="twi-lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
          <img
            src={lightbox}
            alt="Enlarged photo"
            className="twi-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

// ─── RSVP Section ────────────────────────────────────────────────────────────

interface RsvpProps {
  invitationId?: string | null;
  isLive?: boolean;
  rsvpLink?: string;
}

const RsvpSection: React.FC<RsvpProps> = ({ invitationId, isLive, rsvpLink }) => {
  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState<RsvpStatus>('ACCEPTED');
  const [name, setName] = useState('');
  const [headcount, setHeadcount] = useState(1);
  const [message, setMessage] = useState('');

  const sending = state === 'loading';
  const done = state === 'success';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || sending) return;
    submit({
      guestName: name.trim(),
      status,
      headcount: status === 'ACCEPTED' ? headcount : 1,
      message: message.trim() || undefined,
    });
  };

  const STATUS_OPTIONS: { value: RsvpStatus; label: string }[] = [
    { value: 'ACCEPTED', label: 'Joyfully Accepts' },
    { value: 'MAYBE',    label: 'Will Try to Attend' },
    { value: 'DECLINED', label: 'Regretfully Declines' },
  ];

  return (
    <section id="rsvp" className="twi-section twi-section-dark" aria-label="RSVP">
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <span className="twi-eyebrow" style={{ color: 'var(--twi-gold-lt)' }}>Kindly Respond</span>
          <h2 className="twi-section-title" style={{ color: '#FFF8EE' }}>Will You Join Us?</h2>
          <div className="twi-divider" />
        </Reveal>
      </div>

      <Reveal delay={100}>
        <div className="twi-rsvp-inner">
          {done ? (
            <div className="twi-rsvp-thanks" role="status">
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <p>Thank you{name ? `, ${name}` : ''}!</p>
              <p style={{ fontSize: 13, color: 'var(--twi-gold-lt)', marginTop: 8 }}>
                Your RSVP has been recorded. We look forward to celebrating with you.
              </p>
            </div>
          ) : !showForm ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <button
                type="button"
                className="twi-rsvp-submit"
                onClick={() => setShowForm(true)}
              >
                RSVP Now
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="twi-rsvp-seg" role="group" aria-label="Attendance status">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={status === opt.value ? 'twi-on' : ''}
                    onClick={() => setStatus(opt.value)}
                    aria-pressed={status === opt.value}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="twi-rsvp-field">
                <label htmlFor="rsvp-name">Your Name</label>
                <input
                  id="rsvp-name"
                  type="text"
                  required
                  maxLength={120}
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              {status === 'ACCEPTED' && (
                <div className="twi-rsvp-field">
                  <label htmlFor="rsvp-guests">Number of Guests</label>
                  <select
                    id="rsvp-guests"
                    value={headcount}
                    onChange={(e) => setHeadcount(Number(e.target.value))}
                  >
                    {[1,2,3,4,5,6].map((n) => (
                      <option key={n} value={n}>{n} {n === 1 ? 'guest' : 'guests'}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="twi-rsvp-field">
                <label htmlFor="rsvp-message">A Message for the Couple (optional)</label>
                <textarea
                  id="rsvp-message"
                  rows={2}
                  maxLength={400}
                  placeholder="Share your wishes…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              {state === 'error' && <p className="twi-rsvp-err" role="alert">{errorMessage}</p>}

              <button
                type="submit"
                className="twi-rsvp-submit"
                disabled={sending || !name.trim()}
              >
                {sending ? 'Sending…' : 'Send RSVP'}
              </button>

              {rsvpLink && (
                <p style={{ marginTop: 12, fontSize: 12, textAlign: 'center' }}>
                  <a
                    href={rsvpLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--twi-gold-lt)', textDecoration: 'none' }}
                  >
                    Or RSVP via external link <ExternalLink size={10} style={{ display: 'inline' }} />
                  </a>
                </p>
              )}
            </form>
          )}
        </div>
      </Reveal>
    </section>
  );
};

// ─── Footer ───────────────────────────────────────────────────────────────────

interface FooterProps {
  groomName: string;
  brideName: string;
  isPremium?: boolean;
}

const Footer: React.FC<FooterProps> = ({ groomName, brideName, isPremium }) => (
  <footer className="twi-footer">
    <div className="twi-divider" style={{ background: 'linear-gradient(90deg, transparent, var(--twi-gold), transparent)' }} />
    <p style={{ fontSize: 14, marginBottom: 6 }}>
      With love &amp; blessings, {groomName} &amp; {brideName}
    </p>
    <p style={{ fontSize: 12, opacity: 0.5, fontStyle: 'italic' }}>
      Thank you for being part of our celebration.
    </p>
    {!isPremium && (
      <div className="twi-footer-brand">
        Made with{' '}
        <a
          href="https://invitecloud.in"
          target="_blank"
          rel="noopener noreferrer"
          className="twi-footer-brand-link"
        >
          InviteCloud.in
        </a>
      </div>
    )}
  </footer>
);

// ─── Scroll Progress ──────────────────────────────────────────────────────────

const ScrollProgress: React.FC = () => {
  useEffect(() => {
    const root = document.documentElement;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = root;
      const pct = scrollHeight > clientHeight
        ? scrollTop / (scrollHeight - clientHeight)
        : 0;
      root.style.setProperty('--twi-read', String(Math.min(1, Math.max(0, pct))));
    };
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return <div className="twi-progress" aria-hidden="true" />;
};

// ─── Main Template Export ─────────────────────────────────────────────────────

export function TraditionalWeddingInteractive({ data }: { data: InvitationData }) {
  const weddingData: WeddingCustomData = ((data.customData || {}) as any).wedding ?? {};

  const groom: WeddingPersonDetails = weddingData.groom ?? {};
  const bride: WeddingPersonDetails = weddingData.bride ?? {};

  const extracted = extractCoupleNames(data.eventName);
  const groomName = groom.fullName || groom.nickname
    || extracted?.groom || 'Groom';
  const brideName = bride.fullName || bride.nickname
    || extracted?.bride || 'Bride';

  const events: WeddingEventItem[] = weddingData.weddingEvents ?? [];
  const groomFamilyCouples: WeddingFamilyCouple[] = weddingData.groomFamilyCouples ?? [];
  const brideFamilyCouples: WeddingFamilyCouple[] = weddingData.brideFamilyCouples ?? [];
  const showGroomFamilyTree = weddingData.showGroomFamilyTree !== false;
  const showBrideFamilyTree = weddingData.showBrideFamilyTree !== false;
  const familyInvitations: FamilyInvitationCard[] = weddingData.familyInvitations ?? [];
  const showFamilyInvitations = weddingData.showFamilyInvitations !== false;

  const showOrganizers = weddingData.showOrganizers === true;
  const organizers: Organizer[] = weddingData.organizers ?? [];
  const showFamilyChildren = weddingData.showFamilyChildren === true;
  const familyChildrenTitle = weddingData.familyChildrenTitle ?? 'Our Little Stars';
  const familyChildren: FamilyChild[] = weddingData.familyChildren ?? [];

  const allowBlessings = weddingData.allowBlessings !== false;
  const blessingsTitle = weddingData.blessingsTitle ?? 'Blessings For The Couple';
  const allowPhotos = weddingData.allowPhotos !== false;
  const allowReactions = weddingData.allowReactions !== false;
  const enableMockBlessings = weddingData.enableMockBlessings === true;
  const mockBlessingsCount = weddingData.mockBlessingsCount ?? 50;
  const blessingsLoadMode = weddingData.blessingsLoadMode ?? 'loadMore';
  const enableVeilEffect = weddingData.enableVeilEffect === true;

  const showPreWedding = weddingData.showPreWeddingStory === true;
  const preWeddingStory = weddingData.preWeddingStory;
  const preWeddingMoments: WeddingMediaItem[] = preWeddingStory?.moments ?? [];

  const showGallery = weddingData.showMediaShowcase === true;
  const mediaShowcase: WeddingMediaItem[] = weddingData.mediaShowcase ?? [];
  const showWeddingSchedule = weddingData.showWeddingSchedule !== false;
  const visibleEventCount = events.length > 0 ? events.filter((evt) => evt.visible !== false).length : 1;

  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const invitationId = data.id ?? data.invitationId ?? null;

  // Order matches the section order below: couple → events → gallery → family tree → organizers → family children → invitations → blessings → rsvp.
  const navSections: NavSection[] = [
    { id: 'couple',      label: 'Couple' },
    ...(showPreWedding ? [{ id: 'pre-wedding', label: 'Our Story' }] : []),
    ...(showWeddingSchedule && visibleEventCount > 0 ? [{ id: 'events', label: 'Events' }] : []),
    ...(showGallery ? [{ id: 'gallery', label: 'Gallery' }] : []),
    { id: 'family-tree', label: 'Family Tree' },
    ...(showOrganizers && organizers.length > 0 ? [{ id: 'organizers', label: 'Hosts' }] : []),
    ...(showFamilyChildren && familyChildren.length > 0 ? [{ id: 'family-children', label: 'Little Stars' }] : []),
    ...(showFamilyInvitations && familyInvitations.length > 0 ? [{ id: 'invitations', label: 'Invitations' }] : []),
    ...(allowBlessings ? [{ id: 'blessings-wall', label: 'Blessings' }] : []),
    { id: 'rsvp',        label: 'RSVP' },
  ];

  return (
    <div className="twi-root">
      <ScrollProgress />
      <FloatingNav sections={navSections} />

      <SacredVerseSection
        bg={weddingData.sacredVerseBg}
        welcomeMessage={weddingData.sacredVerseWelcomeMessage}
        godImage={weddingData.godImage}
        useShubhVivahImage={weddingData.useShubhVivahImage}
        shubhVivahImage={weddingData.shubhVivahImage}
      />

      {enableVeilEffect && weddingData.coupleSectionBg?.imageUrl ? (
        <TrackVIntegration
          effectType="veil"
          sectionId="couple"
        >
          <CoupleSection
            groom={groom}
            bride={bride}
            bg={weddingData.coupleSectionBg}
            useCustomSwastikImage={weddingData.useCustomSwastikImage}
            customSwastikImage={weddingData.swastikImage}
          />
        </TrackVIntegration>
      ) : (
        <CoupleSection
          groom={groom}
          bride={bride}
          bg={weddingData.coupleSectionBg}
          useCustomSwastikImage={weddingData.useCustomSwastikImage}
          customSwastikImage={weddingData.swastikImage}
        />
      )}

      <EventDetailsSection
        groomName={groomName}
        brideName={brideName}
        eventDate={data.eventDate}
        eventVenue={data.eventVenue}
      />

      {showPreWedding && (
        <PreWeddingStorySection
          title={preWeddingStory?.title}
          description={preWeddingStory?.description}
          moments={preWeddingMoments}
        />
      )}

      {showWeddingSchedule && <EventsSection events={events} />}

      {showGallery && <MediaShowcaseSection items={mediaShowcase} />}

      <FamilyTreeSection
        groomCouples={groomFamilyCouples}
        brideCouples={brideFamilyCouples}
        showGroom={showGroomFamilyTree}
        showBride={showBrideFamilyTree}
      />

      <OrganizersSection
        organizers={organizers}
        showOrganizers={showOrganizers}
      />

      <FamilyChildrenSection
        childrenList={familyChildren}
        showChildren={showFamilyChildren}
        title={familyChildrenTitle}
      />

      {showFamilyInvitations && (
        <FamilyInvitationsSection cards={familyInvitations} />
      )}

      {allowBlessings && (
        <BlessingsWallSection
          invitationId={invitationId}
          isLive={isLive}
          title={blessingsTitle}
          allowPhotos={allowPhotos}
          allowReactions={allowReactions}
          enableMockBlessings={enableMockBlessings}
          mockBlessingsCount={mockBlessingsCount}
          loadMode={blessingsLoadMode}
        />
      )}

      <RsvpSection
        invitationId={invitationId}
        isLive={isLive}
        rsvpLink={data.rsvpLink}
      />

      <Footer groomName={groomName} brideName={brideName} isPremium={data.isPremium} />
    </div>
  );
}

registry.register({ metadata: META, component: TraditionalWeddingInteractive });
